import React from 'react';
import SectionIntro from '../primitives/SectionIntro';
import HelperNote from '../primitives/HelperNote';
import CurrencyField from '../primitives/CurrencyField';
import Tooltip from '../primitives/Tooltip';
import TextField from '../primitives/TextField';
import CategoryCard, { ICONS } from '../primitives/CategoryCard';

export const EXPENSE_CATEGORIES = [
  {
    key: 'groceries',
    label: 'Groceries',
    tip: 'Supermarket food, fresh produce, cleaning products, toiletries and household consumables.',
  },
  {
    key: 'clothing',
    label: 'Clothing & personal care',
    tip: 'Clothing, footwear, haircuts, cosmetics, personal grooming and toiletries.',
  },
  {
    key: 'transport',
    label: 'Transport',
    tip: 'Public transport, fuel, vehicle registration, servicing, parking, tolls, ride-shares.',
  },
  {
    key: 'communication',
    label: 'Phone, internet & subscriptions',
    tip: 'Mobile and home phone bills, internet, pay TV, streaming services and software subscriptions.',
  },
  {
    key: 'recreation',
    label: 'Recreation & entertainment',
    tip: 'Dining out, takeaway, hobbies, gym, sports, holidays and going-out expenses.',
  },
  {
    key: 'child_care',
    label: 'Child care',
    tip: 'Day care, after-school care, nannies and other paid childcare.',
    optional: true,
  },
  {
    key: 'pet_care',
    label: 'Pet care',
    tip: 'Pet food, vet bills, grooming, pet insurance and boarding.',
    optional: true,
  },
  {
    key: 'health',
    label: 'Medical & health',
    tip: 'Out-of-pocket medical, dental, optical, physio and pharmacy not covered by insurance.',
  },
  {
    key: 'health_insurance',
    label: 'Health insurance',
    tip: 'Private health insurance premiums.',
  },
  {
    key: 'general_insurance',
    label: 'General insurance',
    tip: 'Car, home, contents, landlord and travel insurance premiums.',
    optional: true,
  },
  {
    key: 'sickness',
    label: 'Life & income protection insurance',
    tip: 'Life, income protection, accident, sickness and total/permanent disability insurance.',
    optional: true,
  },
  {
    key: 'public_school',
    label: 'Public school education',
    tip: 'Government school fees, uniforms, books, excursions and after-school activities.',
    optional: true,
  },
  {
    key: 'private_school',
    label: 'Private school education & tutoring',
    tip: 'Private school fees, uniforms, books, tutoring and extracurricular fees.',
    optional: true,
  },
  {
    key: 'higher_edu',
    label: 'Higher education & professional fees',
    tip: 'University fees, course fees, professional memberships and ongoing CPD.',
    optional: true,
  },
  {
    key: 'primary_res_running_costs',
    label: 'Primary residence running costs',
    tip: 'Electricity, gas, water, maintenance for the home you live in. NOT rent or loan repayments.',
  },
  {
    key: 'primary_res_strata',
    label: 'Primary residence strata, council & water rates',
    tip: 'Strata fees, body corporate fees, council rates and water rates for your main home.',
  },
  {
    key: 'investment_running',
    label: 'Investment property running costs',
    tip: 'Running costs (rates, strata, maintenance) for any investment properties.',
    optional: true,
  },
  {
    key: 'secondary_res_running',
    label: 'Secondary residence running costs',
    tip: 'Running costs for a holiday home or second residence.',
    optional: true,
  },
];

// Visual grouping for the page. Each group becomes its own card.
const EXPENSE_GROUPS = [
  {
    id: 'day_to_day',
    title: 'Day-to-day living',
    description: 'The everyday spending that keeps the household running.',
    icon: ICONS.cart,
    keys: ['groceries', 'clothing', 'transport', 'communication', 'recreation'],
  },
  {
    id: 'care',
    title: 'Care',
    description: 'Costs for children and pets.',
    icon: ICONS.dependents,
    keys: ['child_care', 'pet_care'],
  },
  {
    id: 'health_insurance',
    title: 'Health & insurance',
    description: 'Out-of-pocket medical costs and insurance premiums.',
    icon: ICONS.shield,
    keys: ['health', 'health_insurance', 'general_insurance', 'sickness'],
  },
  {
    id: 'education',
    title: 'Education',
    description: 'Schooling, tutoring and ongoing study costs.',
    icon: ICONS.book,
    keys: ['public_school', 'private_school', 'higher_edu'],
  },
  {
    id: 'property',
    title: 'Property running costs',
    description: 'Utilities, rates and upkeep for the homes you own or live in - NOT loan repayments.',
    icon: ICONS.wrench,
    keys: ['primary_res_running_costs', 'primary_res_strata', 'investment_running', 'secondary_res_running'],
  },
];

const CATEGORY_BY_KEY = EXPENSE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.key] = cat;
  return acc;
}, {});

class StepLivingExpenses extends React.Component {
  patch = (field, value) => {
    this.props.onUpdateExpenses({ [field]: value });
  };

  total = () => {
    const { expenses } = this.props;
    let t = 0;
    EXPENSE_CATEGORIES.forEach((c) => {
      const v = parseFloat(expenses[c.key]);
      if (!isNaN(v)) t += v;
    });
    const otherAmt = parseFloat(expenses.other_amount);
    if (!isNaN(otherAmt)) t += otherAmt;
    return t;
  };

  formatTotal = (n) => {
    if (!n || isNaN(n)) return '0';
    return n.toLocaleString('en-AU', { maximumFractionDigits: 0 });
  };

  renderRow = (cat) => {
    const { expenses, errors } = this.props;
    const err = errors || {};
    return (
      <div
        key={cat.key}
        className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-2 sm:gap-4 sm:items-center rounded-xl px-2 py-2.5 hover:bg-[#F9FAFB] transition-colors"
      >
        <div className="flex items-start sm:items-center">
          <span className="text-sm sm:text-base text-[#1F2937] font-medium">
            {cat.label}
          </span>
          <Tooltip text={cat.tip} />
          {cat.optional ? (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-[#6B7280]">
              Optional
            </span>
          ) : null}
        </div>
        <CurrencyField
          ariaLabel={cat.label}
          value={expenses[cat.key]}
          onChange={(v) => this.patch(cat.key, v)}
          suffix="/month"
          required={!cat.optional}
          fieldId={`expenses.${cat.key}`}
          error={err[`expenses.${cat.key}`]}
        />
      </div>
    );
  };

  render() {
    const { expenses } = this.props;
    const total = this.total();

    return (
      <div className="space-y-5 pb-24 lg:pb-0">
        {/* Intro */}
        <div className="rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-8">
          <SectionIntro
            eyebrow="About your finances - Living expenses"
            title="Monthly living expenses"
            description="Enter every household expense after your new loan settles - excluding the new loan repayment itself. Tap the i next to each row for a quick explanation of what to include."
          />
          <HelperNote>
            <strong>Tip:</strong> These are <em>combined</em> household expenses for all applicants.
            If you're unsure, refer to a recent bank statement or your typical month. We'll
            auto-calculate the total at the bottom.
          </HelperNote>
        </div>

        {/* Grouped expense cards */}
        {EXPENSE_GROUPS.map((group) => (
          <CategoryCard
            key={group.id}
            icon={group.icon}
            title={group.title}
            description={group.description}
          >
            <div className="space-y-1">
              {group.keys.map((k) => this.renderRow(CATEGORY_BY_KEY[k])).filter(Boolean)}
            </div>
          </CategoryCard>
        ))}

        {/* Other (free-form) */}
        <CategoryCard
          icon={ICONS.other}
          title="Other expenses"
          description="Anything regular that doesn't fit a category above."
        >
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-2 sm:gap-4 sm:items-end">
            <TextField
              label="What is it?"
              value={expenses.other}
              onChange={(v) => this.patch('other', v)}
              placeholder="e.g. court ordered child support"
            />
            <CurrencyField
              label="Monthly amount"
              value={expenses.other_amount}
              onChange={(v) => this.patch('other_amount', v)}
              suffix="/month"
            />
          </div>
        </CategoryCard>

        {/* Sticky total */}
        <div
          className="lg:static lg:mt-2 fixed bottom-0 left-0 right-0 lg:relative bg-white border-t lg:border lg:rounded-2xl border-[#E5E7EB] px-5 py-4 lg:flex lg:items-center lg:justify-between"
          style={{ boxShadow: '0 -4px 8px -4px rgba(0,0,0,0.05)' }}
        >
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-[#6B7280]">
              Estimated monthly expenses
            </p>
            <p className="text-xl sm:text-2xl font-bold mt-0.5" style={{ color: '#5B3FE0' }}>
              ${this.formatTotal(total)} <span className="text-sm font-normal text-[#6B7280]">/ month</span>
            </p>
          </div>
          <div className="hidden lg:block text-xs text-[#6B7280] max-w-xs text-right">
            Auto-calculated from all rows above. Updates live as you type.
          </div>
        </div>
      </div>
    );
  }
}

export default StepLivingExpenses;
